import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import DialogFiles from '@/components/DialogFiles/DialogFiles';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { ASC_ORDER, LONG_FULL_DATE_FORMAT, ROUTES } from '@/helpers/constants';
import { getFullFormattedDate } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useDownloadFile from '@/hooks/useDownloadFile';
import UseGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import documentationServices from '@/services/documentationServices';
import titleService from '@/services/titleService';
import { RouterParams } from '@/types';
import { CampaignDeactivated, File } from '@/types/Files';
import styles from './Files.module.scss';

type FilesProps = {
  timeZone: string;
};

const COLUMNS_NAMES = {
  FILE_NAME: 'fileName',
  SIZE: 'size',
  LAST_MODIFICATION_DATE: 'lastModificationDate',
  DOWNLOAD: 'download',
};

const Files: React.FC<FilesProps> = (props) => {
  const { timeZone } = props;
  const { id } = useParams<RouterParams>();
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { downloadFile } = useDownloadFile();
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  const { addToast } = useToastContext();
  const { goTo } = UseGoTo();
  const [campaignDeactivated, setCampaignDeactivated] = useState<CampaignDeactivated | null>(null);
  const toggleLoadingFiles = (fileId, isLoading = false) => {
    const newFiles = [...files];
    const file = newFiles.find((f) => f.id === fileId);

    if (file) {
      file.loading = isLoading;
      setFiles(newFiles);
    }
  };

  const download = (fileId, fileName) => {
    toggleLoadingFiles(fileId, true);

    downloadFile(fileId, fileName.toString().split('/').pop()).then(() => {
      toggleLoadingFiles(fileId, false);
    });
  };

  const convertSize = (size) => {
    let unit = 'KB';
    let sizeFile = Math.round(size / 1024);

    if (sizeFile > 999) {
      sizeFile = Math.round(size / 1000000);
      unit = 'MB';
    }

    return sizeFile + unit;
  };

  useEffect(() => {
    documentationServices.getCampaignDocuments(id)
      .then((res) => {
        const { documentMetadataList, warningMessage } = res;
        const list = documentMetadataList.map((doc) => ({
          id: doc.id,
          fileName: doc.id.toString().split('/').pop(),
          size: convertSize(doc.size),
          lastModificationDate: getFullFormattedDate(doc.lastModificationDate, timeZone, LONG_FULL_DATE_FORMAT),
          loading: false,
        }));
        setFiles(list);

        if (warningMessage) setCampaignDeactivated(warningMessage);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => { setLoading(false); });
  }, [id]);

  useEffect(() => {
    titleService.updatePageTitle(t('attachments.files'), id);
  }, [id, t, lang]);

  const columns = [
    { name: COLUMNS_NAMES.FILE_NAME, title: t('attachments.fileName'), flex: 0.3 },
    { name: COLUMNS_NAMES.SIZE, title: t('attachments.size'), flex: 0.3 },
    { name: COLUMNS_NAMES.LAST_MODIFICATION_DATE, title: t('attachments.lastModificationDate'), flex: 0.3 },
    { name: COLUMNS_NAMES.DOWNLOAD, title: t('attachments.download'), flex: 0.1, cellRenderer: (params) => params.value },
  ];

  const goBack = () => goTo(ROUTES.FILES_LIST);

  const prepareFiles = () => files.map((file, index) => ({
    id: file.id,
    fileName: file.fileName,
    lastModificationDate: file.lastModificationDate,
    size: file.size,
    download: file.loading
      ? <Spinner isVisible small />
      : (
        <IcoButtonSimple
          id={`btn-download-file-${index}`}
          icon="icon-herunterladen"
          tooltipText={t('attachments.download')}
          handleClick={() => download(file.id, file.fileName)}
        />
      ),
  }));

  return (
    <div className={styles.Content}>
      <div className={styles.FileHeader}>
        <IcoButtonSimple
          id="btn-exit-files"
          icon="icon-pfeil-chevron-links"
          tooltipText={t('campaign.backToOverview')}
          handleClick={goBack}
        />
        <h1>{`${t('campaignRequests.campaign')} ${id} - ${t('attachments.files')}`}</h1>
      </div>
      {loading
        ? <div className={styles.Spinner}><Spinner isVisible /></div>
        : (
          <TableListEditableNew
            list={prepareFiles()}
            columns={columns}
            columnSorting={[{ columnName: COLUMNS_NAMES.FILE_NAME, direction: ASC_ORDER }]}
          />
        )}
      {campaignDeactivated && campaignDeactivated.user && (
        <DialogFiles
          close={() => setCampaignDeactivated(null)}
          data={campaignDeactivated}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  timeZone: getSettingsInfo(state)?.timezone,
});

export default (connect(mapStateToProps, {})(Files));
